import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail, tokenStore } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Building2, Upload, BellRing, KeyRound, DatabaseZap, ListOrdered, ShieldCheck, ChevronUp, ChevronDown, RotateCcw, GripVertical, Image as ImageIcon, CreditCard, Package, Landmark, LogOut, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NAV_ITEMS } from "@/components/Layout";
import { AdminUsersPanel } from "@/pages/AdminUsers";
import BillingPanel from "@/components/BillingPanel";
import AdminPlansPanel from "@/components/AdminPlansPanel";
import IntegrationsPanel from "@/components/IntegrationsPanel";
import { JobSuggestSettings } from "@/components/JobSuggestSettings";

const EMAIL_VARS = ["nazov_servisu", "meno_zakaznika", "cislo_faktury", "suma", "splatnost", "vozidlo", "spz"];
const VAR_LABELS = { nazov_servisu: "Názov servisu", meno_zakaznika: "Meno zákazníka", cislo_faktury: "Číslo faktúry", suma: "Suma", splatnost: "Splatnosť", vozidlo: "Vozidlo", spz: "ŠPZ", dni_po_splatnosti: "Dni po splatnosti" };
const DEF_TPL = {
  invoice: {
    subject: "{{nazov_servisu}} – faktúra č. {{cislo_faktury}}",
    intro: "Dobrý deň, {{meno_zakaznika}},\n\nzasielame Vám faktúru č. {{cislo_faktury}}. Kliknutím na tlačidlo nižšie si stiahnete doklad vo formáte PDF.",
    footer: "V prípade otázok odpovedzte na tento email. Ďakujeme za dôveru.",
  },
  reminder: {
    subject: "Pripomienka platby – faktúra č. {{cislo_faktury}} ({{nazov_servisu}})",
    intro: "Dobrý deň, {{meno_zakaznika}},\n\ndovoľujeme si Vás priateľsky upozorniť, že faktúra č. {{cislo_faktury}} so splatnosťou {{splatnost}} je momentálne {{dni_po_splatnosti}} dní po splatnosti. Ak ste ju už uhradili, tento email prosím ignorujte a ďakujeme.",
    footer: "V prípade otázok odpovedzte na tento email. Ďakujeme.",
  },
};
const empty = { name: "", ico: "", dic: "", ic_dph: "", address: "", city: "", psc: "", country: "SK", iban: "", email: "", phone: "", logo: "", login_background: "", invoice_note: "", labor_rate: "", default_margin: "", oil_change_interval_months: 12, tk_interval_months: 24, ek_interval_months: 24, reminder_enabled: true, reminder_days: 3, warehouse_vat_rate: 23, email_templates: DEF_TPL };
const NAV_BY_ID = Object.fromEntries(NAV_ITEMS.map((n) => [n.id, n]));
const DEFAULT_IDS = NAV_ITEMS.map((n) => n.id);

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);

  const logoutAll = async () => {
    setLogoutAllBusy(true);
    try {
      const { data } = await api.post("/auth/logout-all");
      if (data.access_token) tokenStore.set(data.access_token, data.refresh_token);
      toast.success(data.message || "Všetky ostatné zariadenia boli odhlásené");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLogoutAllBusy(false); }
  };
  const [demoLoading, setDemoLoading] = useState(false);
  const [menuIds, setMenuIds] = useState(DEFAULT_IDS);
  const [menuSaving, setMenuSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [bgSaving, setBgSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTpl = (kind, field, value) => setForm((f) => ({ ...f, email_templates: { ...f.email_templates, [kind]: { ...(f.email_templates?.[kind] || {}), [field]: value } } }));
  const activeField = useRef(null);
  const insertVar = (varName, fallbackKind) => {
    const token = `{{${varName}}}`;
    const a = activeField.current;
    if (a && a.el) {
      const { el, kind, field } = a;
      const cur = form.email_templates?.[kind]?.[field] || "";
      const start = el.selectionStart ?? cur.length;
      const end = el.selectionEnd ?? cur.length;
      setTpl(kind, field, cur.slice(0, start) + token + cur.slice(end));
      requestAnimationFrame(() => { el.focus(); const pos = start + token.length; try { el.setSelectionRange(pos, pos); } catch { /* noop */ } });
    } else if (fallbackKind) {
      const cur = form.email_templates?.[fallbackKind]?.intro || "";
      setTpl(fallbackKind, "intro", cur + token);
    }
  };
  const VarList = ({ kind, extra }) => (
    <div className="flex flex-wrap gap-1.5 pt-1">
      {[...EMAIL_VARS, ...(extra || [])].map((v) => (
        <button key={v} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insertVar(v, kind)}
          className="rounded bg-muted px-1.5 py-0.5 text-[11px] text-primary transition-colors hover:bg-primary/20" data-testid={`var-${v}`}>
          {VAR_LABELS[v] || v}
        </button>
      ))}
    </div>
  );

  const load = useCallback(async () => {
    const { data } = await api.get("/settings");
    const d = data || {};
    setForm({ ...empty, ...d, email_templates: { invoice: { ...DEF_TPL.invoice, ...(d.email_templates?.invoice || {}) }, reminder: { ...DEF_TPL.reminder, ...(d.email_templates?.reminder || {}) } } });
    if (Array.isArray(data?.nav_order) && data.nav_order.length) {
      const valid = data.nav_order.filter((id) => NAV_BY_ID[id]);
      DEFAULT_IDS.forEach((id) => { if (!valid.includes(id)) valid.push(id); });
      setMenuIds(valid);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("Logo max 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => set("logo", reader.result);
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/settings", { ...form, reminder_days: Number(form.reminder_days) || 3, oil_change_interval_months: Number(form.oil_change_interval_months) || 12, tk_interval_months: Number(form.tk_interval_months) || 24, ek_interval_months: Number(form.ek_interval_months) || 24 }); toast.success("Nastavenia uložené"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    if ((pw.next || "").length < 6) { toast.error("Nové heslo musí mať aspoň 6 znakov"); return; }
    if (pw.next !== pw.confirm) { toast.error("Heslá sa nezhodujú"); return; }
    setPwSaving(true);
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); toast.success("Heslo bolo zmenené"); setPw({ current: "", next: "", confirm: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPwSaving(false); }
  };

  const seedDemo = async () => {
    setDemoLoading(true);
    try {
      const { data } = await api.post("/dev/seed-demo");
      toast.success(data.skipped ? "Ukážkové dáta už existujú" : `Vytvorené: ${data.customers} zákazníkov, ${data.vehicles} vozidiel, ${data.jobs} zákaziek, ${data.invoices} faktúr, ${data.warehouse_items} skladových položiek, ${data.employees} zamestnancov, ${data.expenses} výdavkov`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDemoLoading(false); }
  };

  const clearDemo = async () => {
    setDemoLoading(true);
    try { await api.post("/dev/clear-demo"); toast.success("Ukážkové dáta odstránené"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDemoLoading(false); }
  };

  const moveMenu = (idx, dir) => {
    setMenuIds((ids) => {
      const j = idx + dir;
      if (j < 0 || j >= ids.length) return ids;
      const next = [...ids];
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const dropMenu = (targetId) => {
    setMenuIds((ids) => {
      if (!dragId || dragId === targetId) return ids;
      const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
      if (from < 0 || to < 0) return ids;
      const next = [...ids];
      next.splice(from, 1);
      next.splice(to, 0, dragId);
      return next;
    });
    setDragId(null);
  };

  const saveMenu = async () => {
    setMenuSaving(true);
    try {
      await api.put("/settings", { nav_order: menuIds });
      localStorage.setItem("nav_order", JSON.stringify(menuIds));
      window.dispatchEvent(new Event("nav-order-changed"));
      toast.success("Poradie ponuky uložené");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setMenuSaving(false); }
  };

  const resetMenu = () => setMenuIds(DEFAULT_IDS);

  const onLoginBg = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Pozadie max 4MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBgSaving(true);
      try { await api.put("/settings", { login_background: reader.result }); set("login_background", reader.result); toast.success("Prihlasovacie pozadie uložené"); }
      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      finally { setBgSaving(false); }
    };
    reader.readAsDataURL(file);
  };

  const removeLoginBg = async () => {
    setBgSaving(true);
    try { await api.put("/settings", { login_background: "" }); set("login_background", ""); toast.success("Pozadie odstránené (obnoví sa predvolené)"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBgSaving(false); }
  };

  return (
    <div className="max-w-4xl">
      <h1 className="mb-6 font-heading text-3xl font-extrabold uppercase tracking-wide sm:text-4xl">Nastavenia</h1>
      <Tabs defaultValue="udaje" className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-muted/60 p-1">
          <TabsTrigger value="udaje" className="min-h-[40px]" data-testid="settings-tab-udaje"><Building2 className="mr-2 h-4 w-4" />Údaje</TabsTrigger>
          <TabsTrigger value="email" className="min-h-[40px]" data-testid="settings-tab-email"><Mail className="mr-2 h-4 w-4" />E-mail</TabsTrigger>
          <TabsTrigger value="heslo" className="min-h-[40px]" data-testid="settings-tab-heslo"><KeyRound className="mr-2 h-4 w-4" />Zmena hesla</TabsTrigger>
          <TabsTrigger value="menu" className="min-h-[40px]" data-testid="settings-tab-menu"><ListOrdered className="mr-2 h-4 w-4" />Hlavná ponuka</TabsTrigger>
          <TabsTrigger value="demo" className="min-h-[40px]" data-testid="settings-tab-demo"><DatabaseZap className="mr-2 h-4 w-4" />Ukážkové dáta</TabsTrigger>
          <TabsTrigger value="predplatne" className="min-h-[40px]" data-testid="settings-tab-billing"><CreditCard className="mr-2 h-4 w-4" />Predplatné</TabsTrigger>
          <TabsTrigger value="integracie" className="min-h-[40px]" data-testid="settings-tab-integracie"><Landmark className="mr-2 h-4 w-4" />Integrácie</TabsTrigger>
        </TabsList>

        {/* ÚDAJE */}
        <TabsContent value="udaje">
          <div className="card-surface space-y-5 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><Building2 className="h-5 w-5 text-primary" /> Údaje servisu (dodávateľ)</h3>
            <div className="space-y-1.5"><Label>Názov servisu</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="settings-name" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>IČO</Label><Input value={form.ico} onChange={(e) => set("ico", e.target.value)} data-testid="settings-ico" /></div>
              <div className="space-y-1.5"><Label>DIČ</Label><Input value={form.dic} onChange={(e) => set("dic", e.target.value)} data-testid="settings-dic" /></div>
              <div className="space-y-1.5"><Label>IČ DPH</Label><Input value={form.ic_dph} onChange={(e) => set("ic_dph", e.target.value)} data-testid="settings-icdph" /></div>
            </div>
            <div className="space-y-1.5"><Label>Adresa</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="settings-address" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>PSČ</Label><Input value={form.psc} onChange={(e) => set("psc", e.target.value)} data-testid="settings-psc" /></div>
              <div className="space-y-1.5"><Label>Mesto</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="settings-city" /></div>
            </div>
            <div className="space-y-1.5"><Label>IBAN (pre QR PAY by square)</Label><Input value={form.iban} onChange={(e) => set("iban", e.target.value)} className="font-mono" placeholder="SK00 0000 0000 0000 0000 0000" data-testid="settings-iban" /></div>
            <div className="space-y-1.5"><Label>Sadzba za normohodinu (€/h)</Label><Input type="number" step="0.5" value={form.labor_rate} onChange={(e) => set("labor_rate", e.target.value)} placeholder="napr. 40" data-testid="settings-labor-rate" /></div>
            <div className="space-y-1.5"><Label>Predvolená marža skladu (%)</Label><Input type="number" step="1" min="0" value={form.default_margin} onChange={(e) => set("default_margin", e.target.value)} placeholder="napr. 30" data-testid="settings-default-margin" /><p className="text-xs text-muted-foreground">Použije sa ako predvolená prirážka k nákupnej cene pri vkladaní skladových položiek do zákazky (dá sa prepísať).</p></div>
            <div className="space-y-1.5"><Label>Sadzba DPH pre sklad (%)</Label><Input type="number" step="1" min="0" value={form.warehouse_vat_rate} onChange={(e) => set("warehouse_vat_rate", e.target.value)} placeholder="napr. 23" data-testid="settings-warehouse-vat" /><p className="text-xs text-muted-foreground">Používa sa na prepočet nákupných cien v sklade medzi režimom „bez DPH" a „s DPH".</p></div>
            <div className="space-y-1.5"><Label>Interval výmeny oleja (mesiace)</Label><Input type="number" min="1" value={form.oil_change_interval_months} onChange={(e) => set("oil_change_interval_months", e.target.value)} placeholder="napr. 12" data-testid="settings-oil-interval" /><p className="text-xs text-muted-foreground">Po zaškrtnutí „Výmena oleja" v zákazke sa vozidlu pripomenie ďalšia výmena v tomto intervale (upozornenie sa zobrazí mesiac vopred v prehľade).</p></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Platnosť TK (mesiace)</Label><Input type="number" min="1" value={form.tk_interval_months} onChange={(e) => set("tk_interval_months", e.target.value)} placeholder="napr. 24" data-testid="settings-tk-interval" /></div>
              <div className="space-y-1.5"><Label>Platnosť EK (mesiace)</Label><Input type="number" min="1" value={form.ek_interval_months} onChange={(e) => set("ek_interval_months", e.target.value)} placeholder="napr. 24" data-testid="settings-ek-interval" /></div>
            </div>
            <p className="text-xs text-muted-foreground">Keď zákazka obsahuje TK/EK, po uložení sa spýtame, či ju priradiť k vozidlu. Platnosť sa nastaví podľa týchto intervalov a pripomenie sa mesiac vopred.</p>
            <JobSuggestSettings form={form} set={set} />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="settings-email" /></div>
              <div className="space-y-1.5"><Label>Telefón</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="settings-phone" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Logo servisu (na faktúre)</Label>
              <div className="flex items-center gap-4">
                {form.logo && <img src={form.logo} alt="logo" className="h-14 rounded border border-border bg-white p-1" />}
                <label className="flex min-h-[48px] cursor-pointer items-center gap-2 rounded-lg border border-dashed border-border px-4 text-sm text-muted-foreground hover:border-primary" data-testid="settings-logo-label">
                  <Upload className="h-4 w-4" /> Nahrať logo
                  <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="settings-logo-input" />
                </label>
                {form.logo && <Button variant="ghost" size="sm" onClick={() => set("logo", "")}>Odstrániť</Button>}
              </div>
            </div>
            <div className="space-y-1.5"><Label>Poznámka na faktúre</Label><Textarea value={form.invoice_note} onChange={(e) => set("invoice_note", e.target.value)} data-testid="settings-note" /></div>
            <Button onClick={save} disabled={saving} className="min-h-[48px]" data-testid="save-settings-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť nastavenia"}</Button>
          </div>
        </TabsContent>

        {/* E-MAIL */}
        <TabsContent value="email">
          <div className="card-surface space-y-6 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><Mail className="h-5 w-5 text-primary" /> Vzory e-mailov</h3>
            <p className="text-sm text-muted-foreground">Upravte predmet, úvodný text a pätičku. Hlavička s názvom servisu, tabuľka s údajmi faktúry a tlačidlo na PDF sa pridajú automaticky. Premenné v zložených zátvorkách sa pri odoslaní nahradia skutočnými údajmi.</p>

            <div className="rounded-lg border border-border bg-background/40 p-4 space-y-3" data-testid="email-tpl-invoice">
              <div className="font-heading text-sm font-bold uppercase text-primary">Odoslanie faktúry</div>
              <div className="space-y-1.5"><Label>Predmet</Label><Input value={form.email_templates?.invoice?.subject || ""} onFocus={(e) => { activeField.current = { el: e.target, kind: "invoice", field: "subject" }; }} onChange={(e) => setTpl("invoice", "subject", e.target.value)} data-testid="tpl-invoice-subject" /></div>
              <div className="space-y-1.5"><Label>Úvodný text</Label><Textarea rows={4} value={form.email_templates?.invoice?.intro || ""} onFocus={(e) => { activeField.current = { el: e.target, kind: "invoice", field: "intro" }; }} onChange={(e) => setTpl("invoice", "intro", e.target.value)} data-testid="tpl-invoice-intro" /></div>
              <div className="space-y-1.5"><Label>Pätička / poznámka</Label><Textarea rows={2} value={form.email_templates?.invoice?.footer || ""} onFocus={(e) => { activeField.current = { el: e.target, kind: "invoice", field: "footer" }; }} onChange={(e) => setTpl("invoice", "footer", e.target.value)} data-testid="tpl-invoice-footer" /></div>
              <VarList kind="invoice" />
            </div>

            <div className="rounded-lg border border-border bg-background/40 p-4 space-y-3" data-testid="email-tpl-reminder">
              <div className="font-heading text-sm font-bold uppercase text-primary">Upomienka po splatnosti</div>
              <div className="space-y-1.5"><Label>Predmet</Label><Input value={form.email_templates?.reminder?.subject || ""} onFocus={(e) => { activeField.current = { el: e.target, kind: "reminder", field: "subject" }; }} onChange={(e) => setTpl("reminder", "subject", e.target.value)} data-testid="tpl-reminder-subject" /></div>
              <div className="space-y-1.5"><Label>Úvodný text</Label><Textarea rows={5} value={form.email_templates?.reminder?.intro || ""} onFocus={(e) => { activeField.current = { el: e.target, kind: "reminder", field: "intro" }; }} onChange={(e) => setTpl("reminder", "intro", e.target.value)} data-testid="tpl-reminder-intro" /></div>
              <div className="space-y-1.5"><Label>Pätička / poznámka</Label><Textarea rows={2} value={form.email_templates?.reminder?.footer || ""} onFocus={(e) => { activeField.current = { el: e.target, kind: "reminder", field: "footer" }; }} onChange={(e) => setTpl("reminder", "footer", e.target.value)} data-testid="tpl-reminder-footer" /></div>
              <VarList kind="reminder" extra={["dni_po_splatnosti"]} />
            </div>

            <div className="rounded-lg border border-border bg-background/40 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><BellRing className="h-5 w-5 text-primary" /><span className="font-heading text-sm font-bold uppercase">Automatické upomienky</span></div>
                <Switch checked={form.reminder_enabled !== false} onCheckedChange={(v) => set("reminder_enabled", v)} data-testid="settings-reminder-enabled" />
              </div>
              <p className="text-xs text-muted-foreground">Neuhradeným faktúram po splatnosti (s emailom zákazníka) sa raz denne pošle upomienka podľa vzoru vyššie. Znovu najskôr po 7 dňoch. Beží v nasadenej verzii.</p>
              <div className="flex items-center gap-2"><Label className="whitespace-nowrap">Poslať po</Label><Input type="number" min="1" className="w-20" value={form.reminder_days} onChange={(e) => set("reminder_days", e.target.value)} data-testid="settings-reminder-days" /><span className="text-sm text-muted-foreground">dňoch po splatnosti</span></div>
            </div>

            <Button onClick={save} disabled={saving} className="min-h-[48px]" data-testid="save-email-settings-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť nastavenia"}</Button>
          </div>
        </TabsContent>

        {/* ZMENA HESLA */}
        <TabsContent value="heslo">
          <div className="card-surface space-y-4 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><KeyRound className="h-5 w-5 text-primary" /> Zmena hesla</h3>
            <div className="space-y-1.5"><Label>Súčasné heslo</Label><Input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))} data-testid="pw-current" /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>Nové heslo</Label><Input type="password" autoComplete="new-password" value={pw.next} onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))} data-testid="pw-new" /></div>
              <div className="space-y-1.5"><Label>Zopakovať nové heslo</Label><Input type="password" autoComplete="new-password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="pw-confirm" /></div>
            </div>
            <Button onClick={changePassword} disabled={pwSaving} className="min-h-[48px]" data-testid="pw-save-button">{pwSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Zmeniť heslo"}</Button>
            <div className="mt-2 border-t border-border pt-4">
              <h4 className="mb-1 flex items-center gap-2 font-heading text-base font-bold uppercase"><ShieldCheck className="h-4 w-4 text-primary" /> Zabezpečenie</h4>
              <p className="mb-3 text-sm text-muted-foreground">Odhlási vás na všetkých ostatných zariadeniach a prehliadačoch. Toto zariadenie zostane prihlásené.</p>
              <Button variant="outline" onClick={logoutAll} disabled={logoutAllBusy} className="min-h-[48px] gap-2" data-testid="logout-all-button">{logoutAllBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />} Odhlásiť zo všetkých zariadení</Button>
            </div>
          </div>
        </TabsContent>

        {/* HLAVNÁ PONUKA */}
        <TabsContent value="menu">
          <div className="card-surface space-y-4 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><ListOrdered className="h-5 w-5 text-primary" /> Poradie hlavnej ponuky</h3>
            <p className="text-sm text-muted-foreground">Presuňte položky ťahaním myšou (alebo šípkami na dotykových zariadeniach) a nastavte si poradie v bočnom menu podľa seba. Zmena sa prejaví okamžite po uložení.</p>
            <div className="space-y-2">
              {menuIds.map((id, idx) => {
                const item = NAV_BY_ID[id];
                if (!item) return null;
                const Icon = item.icon;
                return (
                  <div key={id} draggable
                    onDragStart={() => setDragId(id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => dropMenu(id)}
                    onDragEnd={() => setDragId(null)}
                    className={`flex items-center justify-between rounded-lg border bg-background/40 px-3 py-2 transition-colors cursor-grab active:cursor-grabbing ${dragId === id ? "border-primary opacity-60" : "border-border"}`}
                    data-testid={`menu-item-${id}`}>
                    <div className="flex items-center gap-3">
                      <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="w-5 text-center text-xs font-mono text-muted-foreground">{idx + 1}</span>
                      <Icon className="h-4 w-4 text-primary" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === 0} onClick={() => moveMenu(idx, -1)} data-testid={`menu-up-${id}`}><ChevronUp className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === menuIds.length - 1} onClick={() => moveMenu(idx, 1)} data-testid={`menu-down-${id}`}><ChevronDown className="h-4 w-4" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={saveMenu} disabled={menuSaving} className="min-h-[48px]" data-testid="menu-save-button">{menuSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť poradie"}</Button>
              <Button variant="outline" onClick={resetMenu} className="min-h-[48px]" data-testid="menu-reset-button"><RotateCcw className="mr-2 h-4 w-4" />Predvolené poradie</Button>
            </div>
          </div>
        </TabsContent>

        {/* UKÁŽKOVÉ DÁTA */}
        <TabsContent value="demo">
          <div className="card-surface space-y-3 p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><DatabaseZap className="h-5 w-5 text-primary" /> Ukážkové dáta</h3>
            <p className="text-sm text-muted-foreground">Naplňte aplikáciu ukážkovými zákazníkmi, vozidlami, zákazkami, faktúrami, skladom, zamestnancami s dochádzkou a výdavkami (rozložené za posledné ~2 mesiace), aby ste videli, ako to vyzerá v plnej prevádzke. Kedykoľvek ich viete odstrániť.</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={seedDemo} disabled={demoLoading} className="min-h-[48px]" data-testid="seed-demo-button">{demoLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Naplniť ukážkové dáta"}</Button>
              <Button variant="outline" onClick={clearDemo} disabled={demoLoading} className="min-h-[48px]" data-testid="clear-demo-button">Odstrániť ukážkové dáta</Button>
            </div>
          </div>
        </TabsContent>

        {/* PREDPLATNÉ */}
        <TabsContent value="predplatne">
          <div className="card-surface p-6"><BillingPanel /></div>
        </TabsContent>

        {/* INTEGRÁCIE */}
        <TabsContent value="integracie">
          <div className="card-surface p-6"><IntegrationsPanel /></div>
        </TabsContent>

      </Tabs>
    </div>
  );
}
